<script>
import VybyColorPicker from '@/components/editor/VybyColorPicker.vue'
import { normalizeHexColor } from '@/editor/colors'

const DISMISSED_HINTS_KEY = 'vyby/hints/dismissed'
const HINTS_RESET_EVENT = 'vyby:hints-reset'
const PAINT_CLEAR_EVENT = 'vyby:paint-clear'
const PAINT_UNDO_EVENT = 'vyby:paint-undo'
const MIN_BRUSH_SIZE = 2
const MAX_BRUSH_SIZE = 64

export default {
  name: 'EditorControllersDock',
  components: {
    VybyColorPicker,
  },
  computed: {
    strokeColor() {
      return normalizeHexColor(this.$store.state.project.settings.strokeColor, '#ffffff')
    },
    paintToolMode() {
      const mode = `${this.$store.state.project.settings.paintToolMode || ''}`.trim()
      return mode === 'brush' ? 'brush' : 'stroke'
    },
    paintBrushColor() {
      return normalizeHexColor(this.$store.state.project.settings.paintBrushColor, '#f37021')
    },
    paintBrushSize() {
      const size = Number(this.$store.state.project.settings.paintBrushSize)
      if (!Number.isFinite(size)) return 12
      return Math.min(MAX_BRUSH_SIZE, Math.max(MIN_BRUSH_SIZE, Math.round(size)))
    },
    activeColor() {
      return this.paintToolMode === 'brush' ? this.paintBrushColor : this.strokeColor
    },
    activeColorTitle() {
      return this.paintToolMode === 'brush' ? 'Brush color' : 'Stroke color'
    },
  },
  methods: {
    setProjectSetting(code, value) {
      this.$store.commit('project/projectSetting', { code, value })
    },
    onStrokeColor(color) {
      const next = normalizeHexColor(color)
      if (!next) return
      this.setProjectSetting('strokeColor', next)
    },
    onBrushColor(color) {
      const next = normalizeHexColor(color)
      if (!next) return
      this.setProjectSetting('paintBrushColor', next)
    },
    onActiveColor(color) {
      if (this.paintToolMode === 'brush') this.onBrushColor(color)
      else this.onStrokeColor(color)
    },
    setPaintToolMode(mode) {
      const next = mode === 'brush' ? 'brush' : 'stroke'
      this.setProjectSetting('paintToolMode', next)
    },
    onBrushSizeInput(e) {
      const raw = Number(e?.target?.value)
      if (!Number.isFinite(raw)) return
      const next = Math.min(MAX_BRUSH_SIZE, Math.max(MIN_BRUSH_SIZE, Math.round(raw)))
      this.setProjectSetting('paintBrushSize', next)
    },
    clearPaint() {
      window.dispatchEvent(new CustomEvent(PAINT_CLEAR_EVENT))
    },
    undoPaint() {
      window.dispatchEvent(new CustomEvent(PAINT_UNDO_EVENT))
    },
    resetHints() {
      try {
        window.localStorage.removeItem(DISMISSED_HINTS_KEY)
      } catch (e) {
        // Ignore storage failures; the reset event still refreshes live hints.
      }
      window.dispatchEvent(new CustomEvent(HINTS_RESET_EVENT))
    },
  },
}
</script>

<template>
  <div class="editor-controllers-dock vyby-glass-panel vyby-glare">
    <div class="dock-header" :style="{ color: strokeColor }">Vybes</div>

    <div class="dock-body">
      <div class="dock-section-title">Paint</div>
      <VybyColorPicker
        class="paint-color-picker"
        :value="activeColor"
        fallback="#ffffff"
        :title="activeColorTitle"
        :size="40"
        :swatch-size="22"
        :live="true"
        :action-buttons="paintToolMode !== 'brush'"
        placement="bottom-start"
        @change="onActiveColor"
      >
        <template #extra>
          <div class="paint-picker-controls" @click.stop @mousedown.stop>
            <div class="paint-mode-toggle" aria-label="Paint target">
              <button
                type="button"
                class="paint-mode-btn"
                :class="{ 'is-active': paintToolMode === 'stroke' }"
                @click="setPaintToolMode('stroke')"
              >
                Stroke
              </button>
              <button
                type="button"
                class="paint-mode-btn"
                :class="{ 'is-active': paintToolMode === 'brush' }"
                @click="setPaintToolMode('brush')"
              >
                Brush
              </button>
            </div>

            <div v-if="paintToolMode === 'brush'" class="brush-size-control">
              <div class="brush-size-row">
                <span>Brush size</span>
                <span>{{ paintBrushSize }}px</span>
              </div>
              <input
                class="brush-size-slider"
                type="range"
                :min="2"
                :max="64"
                :step="1"
                :value="paintBrushSize"
                @input="onBrushSizeInput"
              />
              <div class="paint-history-actions">
                <button type="button" class="dock-paint-action" @click="undoPaint">
                  Undo paint
                </button>
                <button type="button" class="dock-paint-action" @click="clearPaint">
                  Clear paint
                </button>
              </div>
            </div>
          </div>
        </template>
      </VybyColorPicker>
      <button type="button" class="dock-reset-hints" @click="resetHints">
        Reset hints
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.editor-controllers-dock {
  width: 120px;
  min-height: 320px;
  border-radius: 12px;
  padding: 10px 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  align-items: center;
  text-align: center;
}

.dock-header {
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.dock-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.dock-section-title {
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 16px;
  color: rgba(255, 255, 255, 0.85);
}

.paint-color-picker :deep(.vyby-color-picker__trigger) {
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 10px;
}

.paint-picker-controls {
  width: 100%;
  flex: 1 0 100%;
  display: flex;
  flex-direction: column;
  gap: 9px;
  order: -1;
}

.paint-mode-toggle {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px;
  padding: 3px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.22);
}

.paint-mode-btn {
  height: 24px;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: rgba(255, 255, 255, 0.68);
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
}

.paint-mode-btn.is-active {
  background: rgba(243, 112, 33, 0.2);
  color: rgba(255, 255, 255, 0.94);
}

.brush-size-control {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.brush-size-row {
  display: flex;
  justify-content: space-between;
  color: rgba(255, 255, 255, 0.78);
  font-size: 11px;
  font-weight: 700;
}

.brush-size-slider {
  width: 100%;
  accent-color: #f37021;
}

.dock-reset-hints,
.dock-paint-action {
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.72);
  font-size: 10px;
  line-height: 1;
  padding: 6px 9px;
  cursor: pointer;
}

.paint-history-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.dock-paint-action {
  width: 100%;
  padding: 7px 9px;
  color: rgba(255, 255, 255, 0.82);
}

.dock-reset-hints:hover,
.dock-paint-action:hover {
  border-color: rgba(65, 184, 255, 0.5);
  color: rgba(255, 255, 255, 0.92);
  background: rgba(65, 184, 255, 0.12);
}
</style>


